import { ClassroomAnalytics } from "../../../../../interfaces/Dashboard";
import { Data } from "../../../../../interfaces/Data";
import { getModuleById } from "../../../../../utils/dataRetrieval";
import { commonMessages } from "../../../../../utils/messages";
import { WeekInfo, ModuleTimeSpent, DateRange } from "../Types";
import { IntlShape } from "react-intl";

export const calculateTotalTimeSpent = (moduleInfo: ModuleTimeSpent[]) => {
    return moduleInfo.reduce(
        (currentTotalTime, module) => currentTotalTime + module.timeSpent,
        0
    );
};

export const generateModulesTimeSpent = (
    week: DateRange,
    weeksInfo: WeekInfo[]
): ModuleTimeSpent[] => {
    const index = findWeekIndexFromRange(week, weeksInfo);

    if (index === -1) return [];
    return weeksInfo[index].modules.map((module) => {
        return {
            name: module.name.slice(0, 2),
            fullName: module.name,
            timeSpent: module.timeSpent,
            id: module.id,
            timeSpentByDay: module.timeSpentByDay,
        };
    });
};

export const aggregateWeeksInfo = (
    classroom: ClassroomAnalytics,
    studentId: string,
    data: Data,
    intl: IntlShape
): WeekInfo[] => {
    const modules = classroom.modulesList;
    let weeklyDateInfoArray = generateWeeklyDateInfo();
    modules.forEach((module) => {
        const isStudent = !!Object.entries(module.students).find(
            ([id]) => id === studentId
        );
        if (!isStudent) return;

        const students = module.students;

        Object.entries(students[studentId].timeSpent).forEach(
            ([date, time]) => {
                const weekIndex = findWeekIndexFromString(
                    date,
                    weeklyDateInfoArray
                );

                if (weekIndex !== -1) {
                    let moduleIndex = weeklyDateInfoArray[
                        weekIndex
                    ].modules.findIndex((m) => m.id === module.id);

                    const dateDay = new Date(date).getDay();
                    const dayIndex = dateDay === 0 ? 6 : dateDay - 1; //We want Sunday to be index 6, not 0

                    if (moduleIndex !== -1) {
                        const currentTimeSpent =
                            weeklyDateInfoArray[weekIndex].modules[moduleIndex]
                                .timeSpent;
                        weeklyDateInfoArray[weekIndex].modules[
                            moduleIndex
                        ].timeSpent = currentTimeSpent + time;

                        weeklyDateInfoArray[weekIndex].modules[
                            moduleIndex
                        ].timeSpentByDay[dayIndex] = time;
                    } else {
                        const weekArray = Array(7).fill(0);
                        weekArray[dayIndex] = time;

                        weeklyDateInfoArray[weekIndex].modules.push({
                            id: module.id,
                            name: generateFullModuleName(module.id, data, intl),
                            timeSpent: time,
                            timeSpentByDay: weekArray,
                        });
                    }
                }
            }
        );
    });
    return weeklyDateInfoArray;
};

const generateFullModuleName = (id: string, data: Data, intl: IntlShape) => {
    const moduleNumber = data.modules.findIndex((m) => m.id === id) + 1;
    const moduleShortName = getModuleById(id, data).title.short!;
    return `${intl
        .formatMessage(commonMessages.module)[0]
        .toUpperCase()}${moduleNumber}${intl.formatMessage(
        commonMessages.colon
    )} ${moduleShortName}`;
};

// Helper function to get the previous Monday
function getPreviousMonday(date: Date): Date {
    const day = date.getDay();
    const diff = day === 0 ? 6 : day - 1; // Adjust if it's Sunday (0)
    const previousMonday = new Date(date);
    previousMonday.setDate(date.getDate() - diff);
    return previousMonday;
}

// Helper function to add days to a date
function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

// Function to generate the array of DateInfo
function generateWeeklyDateInfo(): WeekInfo[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let currentMonday = getPreviousMonday(today); // Start from the most recent Monday
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);

    const dateInfoArray: WeekInfo[] = [];
    let index = 0;

    while (currentMonday > oneYearAgo) {
        const startOfWeek = new Date(currentMonday);
        const endOfWeek = addDays(startOfWeek, 6); // Get the Sunday of the week

        dateInfoArray.push({
            index: index++,
            range: {
                start: startOfWeek,
                end: endOfWeek,
            },
            modules: [], // Empty array for now
        });

        // Move back one week
        currentMonday.setDate(currentMonday.getDate() - 7);
    }

    return dateInfoArray;
}

// Function to check if a date is within a given week range
function isDateInRange(date: Date, start: Date, end: Date): boolean {
    return date >= start && date <= end;
}

// Function to find the index of the week the given date belongs to
function findWeekIndexFromString(
    dateString: string,
    weeklyDateInfoArray: WeekInfo[]
): number {
    const dateToFind = new Date(dateString);

    for (const weekInfo of weeklyDateInfoArray) {
        if (
            isDateInRange(dateToFind, weekInfo.range.start, weekInfo.range.end)
        ) {
            return weekInfo.index;
        }
    }

    return -1; // Return -1 if no matching week is found
}
// Function to find the index of the week the given date belongs to
export function findWeekIndexFromRange(
    range: DateRange,
    weeklyDateInfoArray: WeekInfo[]
): number {
    for (const weekInfo of weeklyDateInfoArray) {
        if (
            range.start === weekInfo.range.start &&
            range.end === weekInfo.range.end
        ) {
            return weekInfo.index;
        }
    }

    return -1; // Return -1 if no matching week is found
}

// Helper function to format the date with leading zeroes for month and day
export function formatDateWithLeadingZeros(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}/${month}/${day}`;
}
